import {
  Document,
  Page,
  Text,
  View,
  StyleSheet,
  Image,
} from "@react-pdf/renderer";
import type { PlanningReportData } from "./types";
import { formatDateString } from "../lib/dates";

// Promosight logo — served from public/ folder
const LOGO_SRC = "/logo.png";

type PlanningReportProps = {
  data: PlanningReportData;
  logoSrc?: string;
};

const styles = StyleSheet.create({
  page: {
    padding: 40,
    fontSize: 10,
    fontFamily: "Helvetica",
  },
  header: {
    marginBottom: 8,
  },
  logoRow: {
    flexDirection: "row",
    alignItems: "center",
    marginBottom: 16,
  },
  logo: {
    width: 48,
    height: 48,
    marginRight: 12,
  },
  title: {
    fontSize: 18,
    fontFamily: "Helvetica-Bold",
  },
  metaGrid: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 4,
    marginBottom: 12,
  },
  metaItem: {
    width: "48%",
    flexDirection: "row",
    marginBottom: 4,
  },
  metaLabel: {
    fontFamily: "Helvetica-Bold",
    marginRight: 4,
  },
  metaValue: {
    flex: 1,
  },
  summaryRow: {
    flexDirection: "row",
    gap: 24,
    marginBottom: 6,
    paddingTop: 8,
    borderTopWidth: 1,
    borderTopColor: "#ddd",
    borderTopStyle: "solid",
  },
  summaryItem: {
    flexDirection: "row",
  },
  summaryLabel: {
    fontFamily: "Helvetica-Bold",
    marginRight: 4,
  },
  // Table
  table: {
    width: "100%",
  },
  tableHeader: {
    flexDirection: "row",
    backgroundColor: "#f3f4f6",
    borderBottomWidth: 1,
    borderBottomColor: "#ccc",
    borderBottomStyle: "solid",
    paddingVertical: 6,
    paddingHorizontal: 4,
  },
  tableRow: {
    flexDirection: "row",
    borderBottomWidth: 0.5,
    borderBottomColor: "#e5e7eb",
    borderBottomStyle: "solid",
    paddingVertical: 5,
    paddingHorizontal: 4,
    minPresenceAhead: 10,
  },
  tableRowAlt: {
    backgroundColor: "#fafafa",
  },
  colCode: { width: "10%" },
  colName: { width: "22%" },
  colBanner: { width: "18%" },
  colCity: { width: "15%" },
  colProvince: { width: "15%" },
  colBudget: { width: "20%", textAlign: "right" },
  headerText: {
    fontFamily: "Helvetica-Bold",
    fontSize: 9,
  },
  cellText: {
    fontSize: 9,
  },
  footer: {
    position: "absolute",
    bottom: 30,
    left: 40,
    right: 40,
    textAlign: "center",
    fontSize: 8,
    color: "#999",
  },
});

function formatDate(iso: string): string {
  // Parse and format date-only string via date-fns (see dates-and-periods PRD, F06)
  return formatDateString(iso);
}

function formatCurrency(value: number | null): string {
  if (value == null) return "—";
  return value.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
  });
}

export function PlanningReport({ data, logoSrc }: PlanningReportProps) {
  return (
    <Document>
      <Page size="A4" style={styles.page}>
        {/* Header — repeats on every page */}
        <View style={styles.header} fixed>
          <View style={styles.logoRow}>
            <Image src={logoSrc ?? LOGO_SRC} style={styles.logo} />
            <Text style={styles.title}>Planning Report</Text>
          </View>

          <View style={styles.metaGrid}>
            <View style={styles.metaItem}>
              <Text style={styles.metaLabel}>Supplier:</Text>
              <Text style={styles.metaValue}>{data.supplier.name}</Text>
            </View>
            <View style={styles.metaItem}>
              <Text style={styles.metaLabel}>Activity Type:</Text>
              <Text style={styles.metaValue}>{data.activityType.name}</Text>
            </View>
            <View style={styles.metaItem}>
              <Text style={styles.metaLabel}>Product:</Text>
              <Text style={styles.metaValue}>
                {data.product.name}
                {data.product.code ? ` (${data.product.code})` : ""}
              </Text>
            </View>
            <View style={styles.metaItem}>
              <Text style={styles.metaLabel}>Confirmed:</Text>
              <Text style={styles.metaValue}>
                {formatDate(data.confirmedAt)}
              </Text>
            </View>
            <View style={styles.metaItem}>
              <Text style={styles.metaLabel}>Period:</Text>
              <Text style={styles.metaValue}>
                {data.period.name} ({formatDate(data.period.start_date)} –{" "}
                {formatDate(data.period.end_date)})
              </Text>
            </View>
          </View>

          <View style={styles.summaryRow}>
            <View style={styles.summaryItem}>
              <Text style={styles.summaryLabel}>Total Budget:</Text>
              <Text>{formatCurrency(data.totalBudget)}</Text>
            </View>
            <View style={styles.summaryItem}>
              <Text style={styles.summaryLabel}>Total Stores:</Text>
              <Text>{data.totalStores}</Text>
            </View>
          </View>
        </View>

        {/* Table */}
        <View style={styles.table}>
          {/* Header row — repeats on every page */}
          <View style={styles.tableHeader} fixed>
            <Text style={[styles.headerText, styles.colCode]}>Store Code</Text>
            <Text style={[styles.headerText, styles.colName]}>Store Name</Text>
            <Text style={[styles.headerText, styles.colBanner]}>Banner</Text>
            <Text style={[styles.headerText, styles.colCity]}>City</Text>
            <Text style={[styles.headerText, styles.colProvince]}>
              Province
            </Text>
            <Text style={[styles.headerText, styles.colBudget]}>Budget</Text>
          </View>

          {/* Data rows */}
          {data.stores.map((store, i) => (
            <View
              key={`${store.referenceNumber}-${i}`}
              wrap={false}
              style={[
                styles.tableRow,
                ...(i % 2 === 1 ? [styles.tableRowAlt] : []),
              ]}
            >
              <Text style={[styles.cellText, styles.colCode]}>
                {store.referenceNumber ?? "—"}
              </Text>
              <Text style={[styles.cellText, styles.colName]}>
                {store.name}
              </Text>
              <Text style={[styles.cellText, styles.colBanner]}>
                {store.bannerName ?? "—"}
              </Text>
              <Text style={[styles.cellText, styles.colCity]}>
                {store.city ?? "—"}
              </Text>
              <Text style={[styles.cellText, styles.colProvince]}>
                {store.province ?? "—"}
              </Text>
              <Text style={[styles.cellText, styles.colBudget]}>
                {formatCurrency(store.budget)}
              </Text>
            </View>
          ))}
        </View>

        {/* Footer */}
        <Text
          style={styles.footer}
          render={({ pageNumber, totalPages }) =>
            `Page ${pageNumber} of ${totalPages}`
          }
          fixed
        />
      </Page>
    </Document>
  );
}
